import { token } from '@/infrastructure/container';
import { EventBus, type EventSubscription } from '@/infrastructure/EventBus';
import { type ILogger } from './Logger';

export type AppBusEvents = {
  TOAST_REQUESTED: { kind: 'success' | 'error' | 'info'; message: string };
  THEME_CHANGED: { theme: 'light' | 'dark' };
  URL_CHANGED: { path: string };
  KEY_DOWN: {
    key: string;
    ctrl: boolean;
    meta: boolean;
    shift: boolean;
    alt: boolean;
    preventDefault: () => void;
  };
};

export interface IAppBus {
  toast(kind: AppBusEvents['TOAST_REQUESTED']['kind'], message: string): void;
  themeChanged(theme: AppBusEvents['THEME_CHANGED']['theme']): void;
  urlChanged(path: string): void;
  keyDown(data: AppBusEvents['KEY_DOWN']): void;
  subscribe<K extends keyof AppBusEvents>(
    event: K,
    handler: (data: AppBusEvents[K]) => void | Promise<void>,
  ): EventSubscription;
}

export const TOKEN_IAppBus = token<IAppBus>('IAppBus');

export class AppBus implements IAppBus {
  private bus: EventBus<AppBusEvents>;

  constructor(logger: ILogger) {
    this.bus = new EventBus<AppBusEvents>(logger);
  }

  toast(kind: AppBusEvents['TOAST_REQUESTED']['kind'], message: string): void {
    this.bus.publish('TOAST_REQUESTED', { kind, message });
  }

  themeChanged(theme: AppBusEvents['THEME_CHANGED']['theme']): void {
    this.bus.publish('THEME_CHANGED', { theme });
  }

  urlChanged(path: string): void {
    this.bus.publish('URL_CHANGED', { path });
  }

  keyDown(data: AppBusEvents['KEY_DOWN']): void {
    this.bus.publish('KEY_DOWN', data);
  }

  subscribe<K extends keyof AppBusEvents>(
    event: K,
    handler: (data: AppBusEvents[K]) => void | Promise<void>,
  ): EventSubscription {
    return this.bus.subscribe(event, handler);
  }

  // Introspection for boot wiring and tests only. Kept off IAppBus so injected stores can't branch
  // on how many others are listening.
  handlerCount<K extends keyof AppBusEvents>(event: K): number {
    return this.bus.handlerCount(event);
  }
}
